import { hasAnalyticsConsent } from "../utils/cookieConsent";
import { analyticsConfig, telemetryConfig } from "./config";
import { ensureAnalyticsProviderReady, syncConsentMode } from "./tracker";

const TELEMETRY_EVENT_PREFIX = "telemetry_";
const TELEMETRY_BUFFER_KEY = "xphub_telemetry_events_v1";
const telemetryBuffer = [];

let runtimeTelemetryStarted = false;

function sanitizeTelemetryPayload(payload) {
  if (!payload || typeof payload !== "object") return {};
  const out = {};

  for (const [key, value] of Object.entries(payload)) {
    if (value === undefined) continue;
    if (value === null) {
      out[key] = null;
      continue;
    }

    if (typeof value === "number") {
      out[key] = Number.isFinite(value) ? Number(value.toFixed(3)) : null;
      continue;
    }

    if (typeof value === "boolean") {
      out[key] = value;
      continue;
    }

    out[key] = String(value).slice(0, 240);
  }

  return out;
}

function currentPath() {
  if (typeof window === "undefined") return "/";
  return `${window.location.pathname || "/"}${window.location.search || ""}`;
}

function persistTelemetryBuffer() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(TELEMETRY_BUFFER_KEY, JSON.stringify(telemetryBuffer));
  } catch {
    // noop
  }
}

function bufferTelemetryEvent(event) {
  telemetryBuffer.push(event);
  if (telemetryBuffer.length > telemetryConfig.maxBufferedEvents) {
    telemetryBuffer.splice(0, telemetryBuffer.length - telemetryConfig.maxBufferedEvents);
  }
  persistTelemetryBuffer();

  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("xphub:telemetry-event", { detail: event }));
    window.__XPHUB_TELEMETRY__ = {
      getEvents: () => [...telemetryBuffer],
    };
  }
}

async function dispatchTelemetryExternally(event) {
  if (!analyticsConfig.enabled || analyticsConfig.provider !== "ga4") return false;
  if (!hasAnalyticsConsent()) return false;

  const ready = await ensureAnalyticsProviderReady();
  if (!ready || typeof window === "undefined" || typeof window.gtag !== "function") {
    return false;
  }

  syncConsentMode();
  window.gtag("event", `${TELEMETRY_EVENT_PREFIX}${event.type}`, {
    app_env: event.app_env,
    path: event.path,
    level: event.level,
    ...event.payload,
  });

  return true;
}

export function recordTelemetry(type, payload = {}, { level = "info" } = {}) {
  if (!telemetryConfig.enabled) return;

  const event = {
    type: String(type || "unknown").toLowerCase(),
    level,
    occurred_at: new Date().toISOString(),
    app_env: analyticsConfig.appEnv,
    path: currentPath(),
    payload: sanitizeTelemetryPayload(payload),
  };

  bufferTelemetryEvent(event);
  dispatchTelemetryExternally(event);

  if (analyticsConfig.debug) {
    // eslint-disable-next-line no-console
    console.info("[telemetry]", event);
  }
}

export function recordWebVitalMetric(metric) {
  if (!telemetryConfig.enabled || !telemetryConfig.captureWebVitals || !metric) return;

  recordTelemetry("web_vital", {
    metric_name: metric.name,
    value: metric.value,
    delta: metric.delta,
    rating: metric.rating || "n/a",
    navigation_type: metric.navigationType || "unknown",
  });
}

function normalizeErrorMessage(reason) {
  if (!reason) return "Unknown error";
  if (typeof reason === "string") return reason;
  if (reason instanceof Error) return reason.message;
  if (typeof reason?.message === "string") return reason.message;
  return String(reason);
}

export function startRuntimeTelemetry() {
  if (!telemetryConfig.enabled || !telemetryConfig.captureRuntimeErrors) return;
  if (runtimeTelemetryStarted || typeof window === "undefined") return;
  runtimeTelemetryStarted = true;

  window.addEventListener(
    "error",
    (event) => {
      recordTelemetry("runtime_error", {
        message: normalizeErrorMessage(event?.error || event?.message),
        source: event?.filename || "unknown",
        line: Number(event?.lineno) || null,
        column: Number(event?.colno) || null,
      }, { level: "error" });
    },
    true
  );

  window.addEventListener("unhandledrejection", (event) => {
    recordTelemetry("unhandled_rejection", {
      message: normalizeErrorMessage(event?.reason),
    }, { level: "error" });
  });
}

export function recordApiMetric({ method, path, status, durationMs, ok, category = "general" }) {
  if (!telemetryConfig.enabled || !telemetryConfig.captureApiMetrics) return;

  const numericDuration = Number(durationMs);
  const isSlow = Number.isFinite(numericDuration) && numericDuration >= telemetryConfig.slowApiThresholdMs;

  recordTelemetry(
    "api_metric",
    {
      method: String(method || "GET").toUpperCase(),
      path: String(path || "unknown"),
      status: Number(status) || 0,
      duration_ms: Number.isFinite(numericDuration) ? numericDuration : null,
      ok: Boolean(ok),
      category,
      is_slow: isSlow,
    },
    { level: isSlow ? "warn" : "info" }
  );
}

export function recordApiError({ method, path, status, errorCode, category = "general" }) {
  if (!telemetryConfig.enabled || !telemetryConfig.captureApiMetrics) return;

  recordTelemetry(
    "api_error",
    {
      method: String(method || "GET").toUpperCase(),
      path: String(path || "unknown"),
      status: Number(status) || 0,
      error_code: errorCode ? String(errorCode) : "unknown",
      category,
    },
    { level: "error" }
  );
}
